import { cn, parseTime } from '../../lib/helpers.ts';
import SmallButton from '../buttons/SmallButton.tsx';
import { Icon, UilLock, UilPause, UilPlay } from '@iconscout/react-unicons';
import { LectureVideoPlayer } from '../video/LectureVideoPlayer.tsx';
import API from '../../lib/api.ts';
import { Color, Lecture } from '../../lib/types.ts';
import { useState } from 'react';
import clsx from 'clsx';
import { RECENTLY_PLAYED_LECTURES_KEY } from '../../lib/fetches.ts';
import { useSWRConfig } from 'swr';

interface RecommendedLectureProps {
    lecture: Lecture;
    color: Color | 'product';
    isLocked: boolean;
    isActive: boolean;
    onPlayClick: () => void;
    onPauseClick: () => void;
}

function RecommendedLecture({
    lecture,
    color,
    isLocked,
    isActive,
    onPlayClick,
    onPauseClick,
}: RecommendedLectureProps) {
    const { mutate: mutateGlobal } = useSWRConfig();

    const { minutes, seconds } = parseTime(lecture.video?.length);

    const PlayButtonIcon = isActive ? UilPause : UilPlay;

    const [autoplay, setAutoplay] = useState(false);

    return (
        <div className="px-4 py-3">
            <div
                className="flex items-center space-x-4"
                onClick={() => {
                    if (isLocked) return;

                    setAutoplay(false);
                    if (!isActive) onPlayClick();
                }}
            >
                <div className="flex flex-1 flex-col">
                    <h5 className={cn(isLocked && 'opacity-50', 'mb-1')}>
                        {lecture.name}
                    </h5>
                    <p className={cn(isLocked && 'opacity-50', 'text-sm')}>
                        {minutes > 0 && <>{minutes}&nbsp;min</>} {seconds}
                        &nbsp;s
                    </p>
                </div>
                <SmallButton
                    variant={isActive ? 'primary' : 'secondary'}
                    color={color}
                    disabled={isLocked}
                    onClick={(e) => {
                        e.stopPropagation();
                        // setAutoplay(true);
                        const callback = isActive ? onPauseClick : onPlayClick;
                        callback();
                    }}
                >
                    {isLocked ? (
                        <UilLock size="20" />
                    ) : (
                        <PlayButtonIcon size="20" />
                    )}
                </SmallButton>
            </div>
            <div
                className={clsx(
                    'h-auto w-full overflow-hidden transition-all',
                    isActive ? 'max-h-96' : 'max-h-0'
                )}
            >
                <LectureVideoPlayer
                    autoPlay={autoplay}
                    isActive={isActive}
                    video={lecture.video}
                    onEnded={() => {
                        API.post(`/lectures/${lecture.slug}/complete`);
                    }}
                    onFirstPlay={() => {
                        API.post(`/lectures/${lecture.slug}/started`).then(
                            () => {
                                mutateGlobal(RECENTLY_PLAYED_LECTURES_KEY);
                            }
                        );
                    }}
                />
            </div>
        </div>
    );
}

interface RecommendedLecturesPlayableProps {
    color: Color | 'product';
    heading: string;
    slug: string;
    icon: Icon;
    lectures: Lecture[];
    activeLecture: string | null;
    setActiveLecture: (slug: string | null) => void;
}

export function RecommendedLecturesPlayableCard({
    color,
    heading,
    slug,
    icon: Icon,
    lectures,
    activeLecture,
    setActiveLecture,
}: RecommendedLecturesPlayableProps) {
    const colorVariants = {
        product: {
            header: 'bg-product-100 text-product-600',
        },
        blue: {
            header: 'bg-blue-100 text-blue-600',
        },
        green: {
            header: 'bg-green-100 text-green-600',
        },
        orange: {
            header: 'bg-orange-100 text-orange-600',
        },
        red: {
            header: 'bg-red-100 text-red-600',
        },
        turquoise: {
            header: 'bg-turquoise-100 text-turquoise-600',
        },
        purple: {
            header: 'bg-purple-100 text-purple-600',
        },
        rose: {
            header: 'bg-rose-100 text-rose-600',
        },
    };

    return (
        <div>
            <div className="w-full rounded-2xl bg-white shadow-sm">
                {/* Header */}
                <div
                    className={cn(
                        'flex items-center rounded-t-2xl px-4 py-2',
                        colorVariants[color]['header']
                    )}
                >
                    <Icon size="24" className="me-2" />
                    <h4 className="mb-0 font-sans text-base font-medium">
                        {heading}
                    </h4>
                </div>
                {/* Content */}
                <div className="flex flex-col divide-y divide-cloud-200">
                    {lectures?.map((lecture) => (
                        <RecommendedLecture
                            key={lecture.slug}
                            lecture={lecture}
                            color={color}
                            isLocked={lecture.is_locked}
                            isActive={
                                `${slug}-${lecture.slug}` === activeLecture
                            }
                            onPlayClick={() => {
                                setActiveLecture(`${slug}-${lecture.slug}`);
                            }}
                            onPauseClick={() => {
                                setActiveLecture(null);
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
